
if (!customElements.get('discount-progress-bar')) {
	class DiscountProgressBar extends HTMLElement {
		constructor() {
			super();

			this.state = {
				cartTotal: 0,
				cartQuantity: 0,
				currencySymbol: '$',
				progressBarType: 'amount',
				goals: [],
				currentGoalIndex: -1,
				previousGoalIndex: -1,
				progressPercentage: 0
			};

			this.elements = {
				progressFill: null,
				progressText: null,
				milestones: []
			};

			this.animationFrameId = null;
			this.updateTimer = null;
		}

		connectedCallback() {
			this.initializeComponent();
			this.setupEventListeners();
			this.updateProgressBar();
		}

		disconnectedCallback() {
			this.cleanup();
		}

		initializeComponent() {
			const cartTotalAttr = this.getAttribute('data-cart-total');
			const cartQuantityAttr = this.getAttribute('data-cart-quantity');
			this.state.cartTotal = cartTotalAttr ? parseFloat(cartTotalAttr) : 0;
			this.state.cartQuantity = cartQuantityAttr ? parseInt(cartQuantityAttr) : 0;
			this.state.currencySymbol = this.getAttribute('data-currency-symbol') || '$';
			this.state.progressBarType = this.getAttribute('data-progress-bar-type') || 'amount';
			this.elements.progressFill = this.querySelector('.progress-bar-fill');
			this.elements.progressText = this.querySelector('.progress-text');
			this.elements.milestones = Array.from(this.querySelectorAll('.progress-milestone'));


			this.state.goals = this.elements.milestones
				.map((milestone, index) => {
					let goalAmount = 0;
					if (this.state.progressBarType === 'quantity') {
						goalAmount = parseInt(milestone.getAttribute('data-goal')) || 0;
					} else {
						goalAmount = parseFloat(Shopify.currency.rate) * parseFloat(milestone.getAttribute('data-goal')) || 0;
					}
					if (goalAmount <= 0) return null;

					return {
						index,
						amount: goalAmount,
						element: milestone,
						title: milestone.querySelector('.milestone-discount')?.textContent || '',
						description: milestone.querySelector('.milestone-label')?.textContent || '',
						messageBefore: this.getGoalMessage(index + 1, 'before'),
						messageReached: this.getGoalMessage(index + 1, 'reached')
					};
				})
				.filter(goal => goal !== null)
				.sort((a, b) => a.amount - b.amount);

			if (this.state.goals.length === 0) {
				console.warn('DiscountProgressBar: No valid goals configured');
			}
		}

		getGoalMessage(goalNumber, type) {
			const script = this.querySelector(`script[data-goal-${goalNumber}-${type}]`);
			return script ? script.textContent.trim() : '';
		}

		setupEventListeners() {
			document.addEventListener('cart:updated', this.handleCartUpdate.bind(this));
			document.addEventListener('cart:change', this.handleCartUpdate.bind(this));
			document.addEventListener('variant:change', this.handleCartUpdate.bind(this));
			document.addEventListener('shopify:section:load', this.handleSectionReload.bind(this));
		}

		handleCartUpdate(event) {
			let newTotal = this.state.cartTotal;
			let newQuantity = this.state.cartQuantity;

			if (event.detail && event.detail.cart) {
				newTotal = this.extractCartTotal(event.detail.cart);
				newQuantity = this.extractCartQuantity(event.detail.cart);
			} else {
				this.fetchCartAndUpdate();
				return;
			}

			this.updateCartData(newTotal, newQuantity);
		}

		extractCartTotal(cart) {
			if (typeof cart.original_total_price === 'number') {
				return cart.total_price / 100;
			}
			if (typeof cart.original_total_price === 'string') {
				return parseFloat(cart.original_total_price.replace(/[^0-9.]/g, ''));
			}
			return this.state.cartTotal;
		}

		extractCartQuantity(cart) {
			if (Array.isArray(cart.items)) {
				return cart.items.reduce((total, item) => total + (item.quantity || 0), 0);
			}
			return this.state.cartQuantity;
		}

		async fetchCartAndUpdate() {
			try {
				const response = await fetch('/cart.js');
				const cart = await response.json();
				const newTotal = this.extractCartTotal(cart);
				const newQuantity = this.extractCartQuantity(cart);
				this.updateCartData(newTotal, newQuantity);
			} catch (error) {
				console.error('DiscountProgressBar: Error fetching cart', error);
			}
		}

		updateCartData(newTotal, newQuantity) {
			const totalsChanged = this.state.cartTotal !== newTotal || this.state.cartQuantity !== newQuantity;

			if (!totalsChanged) return;

			this.state.cartTotal = newTotal;
			this.state.cartQuantity = newQuantity;
			this.setAttribute('data-cart-total', newTotal);

			clearTimeout(this.updateTimer);
			this.updateTimer = setTimeout(() => {
				this.updateProgressBar();
			}, 100);
		}

		updateProgressBar() {
			if (!this.elements.progressFill || this.state.goals.length === 0) return;
			this.calculateProgress();
			this.animateProgressBar();
			this.updateMilestones();
			this.updateMessage();
		}

		calculateProgress() {
			const currentValue = this.state.progressBarType === 'quantity' ? this.state.cartQuantity : this.state.cartTotal;
			this.state.previousGoalIndex = this.state.currentGoalIndex;

			let currentGoalIndex = -1;
			let progressPercentage = 0;
			for (let i = 0; i < this.state.goals.length; i++) {
				const goal = this.state.goals[i];

				if (currentValue >= goal.amount) {
					currentGoalIndex = i;
				} else {
					const previousGoal = i > 0 ? this.state.goals[i - 1] : { amount: 0 };
					const range = goal.amount - previousGoal.amount;
					const progress = currentValue - previousGoal.amount;
					const goalWidth = 100 / this.state.goals.length;
					progressPercentage = (i * goalWidth) + ((progress / range) * goalWidth);
					progressPercentage = Math.max(0, Math.min(100, progressPercentage));
					break;
				}
			}

			if (currentGoalIndex === this.state.goals.length - 1) {
				progressPercentage = 100;
			}

			this.state.currentGoalIndex = currentGoalIndex;
			this.state.progressPercentage = progressPercentage;
		}

		animateProgressBar() {
			const progressFill = this.elements.progressFill;
			const percentage = this.state.progressPercentage;
			const goalJustReached = this.state.currentGoalIndex > this.state.previousGoalIndex;
			requestAnimationFrame(() => {
				progressFill.style.width = `${percentage}%`;
				progressFill.setAttribute('data-progress', percentage.toFixed(2));

				if (goalJustReached) {
					progressFill.classList.add('goal-reached');
					setTimeout(() => {
						progressFill.classList.remove('goal-reached');
					}, 600);
				}
			});
		}

		updateMilestones() {
			this.state.goals.forEach((goal, index) => {
				const element = goal.element;

				element.classList.remove('active', 'reached', 'next-goal');

				if (index < this.state.currentGoalIndex) {
					element.classList.add('reached');
				} else if (index === this.state.currentGoalIndex) {
					element.classList.add('active', 'reached');
				} else if (index === this.state.currentGoalIndex + 1) {
					element.classList.add('next-goal');
				}
			});
		}

		updateMessage() {
			if (!this.elements.progressText) return;

			const currentValue = this.state.progressBarType === 'quantity' ? this.state.cartQuantity : this.state.cartTotal;
			const nextGoalIndex = this.state.currentGoalIndex + 1;

			let message = '';
			if (this.state.currentGoalIndex >= this.state.goals.length - 1 &&
				currentValue >= this.state.goals[this.state.goals.length - 1].amount) {
				const lastGoal = this.state.goals[this.state.goals.length - 1];
				message = lastGoal.messageReached || 'Congratulations! You\'ve unlocked all rewards!';
			}
			else if (nextGoalIndex < this.state.goals.length) {
				const nextGoal = this.state.goals[nextGoalIndex];
				const amountNeeded = nextGoal.amount - currentValue;

				if (amountNeeded > 0) {
					message = nextGoal.messageBefore || '';
					let formattedAmount;
					
					if (this.state.progressBarType === 'quantity') {
						formattedAmount = `<strong>${Math.ceil(amountNeeded)}</strong>`;
					} else {
						formattedAmount = `<strong>${this.formatCurrency(amountNeeded)}</strong>`;
					}
					
					message = message.replace(/\{amount\}/g, formattedAmount);
				} else {
					message = nextGoal.messageReached || '';
				}
			}

			this.updateMessageText(message);
		}

		updateMessageText(newMessage) {
			const textElement = this.elements.progressText;
			const currentMessage = textElement.textContent.trim();
			if (currentMessage === newMessage) return;
			textElement.classList.remove('visible');

			setTimeout(() => {
				textElement.innerHTML = `<span>${newMessage}</span>`;

				requestAnimationFrame(() => {
					textElement.classList.add('visible');
				});
			}, 200);
		}


		formatCurrency(amount) {
			const formatted = amount;
			return `${this.state.currencySymbol}${formatted}`;
		}

		handleSectionReload() {
			this.initializeComponent();
			this.updateProgressBar();
		}

		cleanup() {
			clearTimeout(this.updateTimer);

			if (this.animationFrameId) {
				cancelAnimationFrame(this.animationFrameId);
			}

			document.removeEventListener('cart:updated', this.handleCartUpdate);
			document.removeEventListener('cart:change', this.handleCartUpdate);
			document.removeEventListener('variant:change', this.handleCartUpdate);
			document.removeEventListener('shopify:section:load', this.handleSectionReload);
		}

		setCartTotal(newTotal) {
			this.updateCartData(newTotal, this.state.cartQuantity);
		}

		getState() {
			return { ...this.state };
		}
	}

	customElements.define('discount-progress-bar', DiscountProgressBar);
}
